// @ts-strict-ignore
import React, { useCallback } from 'react'

import type { PlaceType } from '@dialogue/coredata'
import Icon from 'APP/Converse/Icon'
import { AutocompleteInput, AutocompleteOption } from 'APP/Converse/Inputs/AutocompleteInput'
import { loadPlaceOptions } from 'APP/Lib/PlacesAutocompleteHelpers'
import i18n from 'APP/I18n'

const CURRENT_OPTION: AutocompleteOption = {
  value: 'current',
  label: i18n.t('SelfNavigationScreen.searchLocation.yourLocation'),
  emoji: '📍',
}

export interface Place {
  place_id: string
  name: string
  address?: string
}

export interface PlacePickerProps {
  type?: PlaceType
  value: 'current' | Place | null
  hasCurrentLocation?: boolean
  onChange: (option: 'current' | Place | null) => void
  error?: string
}

export default function PlacePicker({
  type = 'address',
  value,
  hasCurrentLocation,
  onChange,
  error,
}: PlacePickerProps) {
  const loadOptions = useCallback(
    async (input: string, { signal }: { signal: AbortSignal }) => {
      const currentOption = hasCurrentLocation
        ? CURRENT_OPTION
        : {
            ...CURRENT_OPTION,
            caption: i18n.t('SelfNavigationScreen.searchLocation.enablePermissions'),
          }

      const placeOptions = await loadPlaceOptions({ input, placeType: type, signal })
      return [currentOption, ...placeOptions]
    },
    [type, hasCurrentLocation]
  )
  const handleChange = useCallback(
    (option: { value: string; label: string; caption?: string } | null) => {
      if (option == null) {
        onChange(null)
        return
      }

      if (option.value === 'current') {
        onChange('current')
        return
      }

      onChange({ place_id: option.value, name: option.label, address: option.caption })
    },
    [onChange]
  )

  return (
    <AutocompleteInput
      label={i18n.t('SelfNavigationScreen.searchLocation.searchLocation')}
      infotip={i18n.t('SelfNavigationScreen.searchLocation.infotip')}
      showRequiredAsterisk
      isPersistentInfotip
      icon={<Icon src="search" size={20} color="accent" variant="materialIcons" />}
      option={
        value != null
          ? value === 'current'
            ? {
                value: 'current',
                label: i18n.t('SelfNavigationScreen.searchLocation.yourLocation'),
              }
            : { value: value.place_id, label: value.name, caption: value.address }
          : null
      }
      onChange={handleChange}
      loadOptions={loadOptions}
      debounceTime={300}
      maxLength={100}
      error={error}
      testID="PLACE_PICKER"
    />
  )
}
